import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listBarna, type Barna } from '../api/barnaApi';
import { listBhed, type Bhed } from '../api/bhedApi';
import { listBarnaSamuha, type BarnaSamuha } from '../api/barnaSamuhaApi';
import { listGana, type Gana } from '../api/ganaApi';
import { listPratyahar, type Pratyahar } from '../api/pratyaharApi';
import { listLanguages, type Language } from '../api/languageApi';
import { listSutraCommentary, type SutraCommentary } from '../api/sutraCommentaryApi';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  createAshtadhyayi,
  deleteAshtadhyayi,
  emptyCreateAshtadhyayiBody,
  getAshtadhyayi,
  listAshtadhyayiPaginated,
  sutraToFormValues,
  updateAshtadhyayi,
  type Adhikar,
  type AnubrittiPadachhedRef,
  type AshtadhyayiSutra,
  type SutraCommentaryText,
  type CreateAshtadhyayiBody,
  type UpdateAshtadhyayiBody,
} from '../api/ashtadhyayiApi';
import {
  createAnubritti,
  deleteAnubritti,
  updateAnubritti,
  type Anubritti,
  type UpdateAnubrittiBody,
} from '../api/ashtadhyayiAnubrittiApi';
import {
  createAdhikar,
  deleteAdhikar,
  updateAdhikar,
  type UpdateAdhikarBody,
} from '../api/ashtadhyayiAdhikarApi';
import {
  createPadachhed,
  deletePadachhed,
  updatePadachhed,
  type Padachhed,
  type UpdatePadachhedBody,
} from '../api/ashtadhyayiPadachhedApi';
import {
  createAshtadhyayiBartik,
  deleteAshtadhyayiBartik,
  updateAshtadhyayiBartik,
  type BartikAdhikar,
  type BartikAnubritti,
  type AshtadhyayiBartik,
} from '../api/ashtadhyayiBartikApi';
import {
  createBartikPadachhed,
  deleteBartikPadachhed,
  updateBartikPadachhed,
  type BartikPadachhed,
  type UpdateBartikPadachhedBody,
} from '../api/ashtadhyayiBartikPadachhedApi';
import {
  createBartikAnubritti,
  deleteBartikAnubritti,
  updateBartikAnubritti,
  type UpdateBartikAnubrittiBody,
} from '../api/ashtadhyayiBartikAnubrittiApi';
import {
  createBartikAdhikar,
  deleteBartikAdhikar,
  updateBartikAdhikar,
  type UpdateBartikAdhikarBody,
} from '../api/ashtadhyayiBartikAdhikarApi';
import { PadachhedBarnaSamuhaLinksPanel } from '../components/PadachhedBarnaSamuhaLinksPanel';
import { PadachhedGanaLinksPanel } from '../components/PadachhedGanaLinksPanel';
import { PadachhedPratyaharLinksPanel } from '../components/PadachhedPratyaharLinksPanel';
import { BartikPadachhedPratyaharLinksPanel } from '../components/BartikPadachhedPratyaharLinksPanel';
import { BartikPadachhedBarnaSamuhaLinksPanel } from '../components/BartikPadachhedBarnaSamuhaLinksPanel';
import { BartikPadachhedGanaLinksPanel } from '../components/BartikPadachhedGanaLinksPanel';
import {
  createSutraCommentaryText,
  listSutraCommentaryText,
  deleteSutraCommentaryText,
  updateSutraCommentaryText,
  type UpdateSutraCommentaryTextBody,
} from '../api/sutraCommentaryTextApi';

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string; status?: number };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

function locationLabel(s: Pick<AshtadhyayiSutra, 'a' | 'p' | 'n'>): string {
  const parts = [s.a, s.p, s.n].map((x) => String(x ?? '').trim()).filter(Boolean);
  return parts.length ? parts.join('–') : '—';
}

type SutraPickerSeed = Pick<AshtadhyayiSutra, 'id' | 'itemKey' | 'sutraText' | 'a' | 'p' | 'n'>;

function sutraPickerLabel(s: SutraPickerSeed): string {
  const loc = locationLabel(s);
  const text = s.sutraText.trim();
  return text ? `${s.itemKey} — ${text} (${loc})` : `${s.itemKey} (#${s.id}, ${loc})`;
}

function buildSutraPickerOptions(seeds: SutraPickerSeed[]): { value: number; label: string }[] {
  const byId = new Map<number, string>();
  for (const s of seeds) {
    if (Number.isFinite(s.id)) byId.set(s.id, sutraPickerLabel(s));
  }
  return Array.from(byId.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function padachhedPickerLabel(p: AnubrittiPadachhedRef): string {
  const lupta = p.digits != null ? ', लुप्त' : '';
  return `${p.text} (#${p.id}, order ${p.order}, bibh ${p.bibhakti}, vac ${p.bachan}${lupta}) — sūtra #${p.ashtadhyayiSutraId}`;
}

function buildPadachhedPickerOptions(seeds: AnubrittiPadachhedRef[]): { value: number; label: string }[] {
  const byId = new Map<number, string>();
  for (const p of seeds) {
    if (Number.isFinite(p.id)) byId.set(p.id, padachhedPickerLabel(p));
  }
  return Array.from(byId.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function bartikPadachhedPickerLabel(
  p: Pick<BartikPadachhed, 'id' | 'order' | 'text' | 'bibhakti' | 'bachan' | 'digits'>
): string {
  const lupta = p.digits != null ? ', लुप्त' : '';
  return `${p.text} (#${p.id}, order ${p.order}, bibh ${p.bibhakti}, vac ${p.bachan}${lupta})`;
}

function buildBartikPadachhedPickerOptions(rows: BartikPadachhed[]): { value: number; label: string }[] {
  return rows
    .map((p) => ({ value: p.id, label: bartikPadachhedPickerLabel(p) }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function padachhedToRef(row: Padachhed): AnubrittiPadachhedRef {
  return {
    id: row.id,
    ashtadhyayiSutraId: row.ashtadhyayiSutraId,
    order: row.order,
    text: row.text,
    bibhakti: row.bibhakti,
    bachan: row.bachan,
    digits: row.digits,
  };
}

type PadachhedFormValues = {
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  lupta: boolean;
};

function padachhedToFormValues(row: Padachhed): PadachhedFormValues {
  return {
    order: row.order,
    text: row.text,
    bibhakti: row.bibhakti,
    bachan: row.bachan,
    lupta: row.digits != null,
  };
}

function nextPadachhedOrder(rows: Padachhed[]): number {
  if (rows.length === 0) return 0;
  return Math.max(...rows.map((r) => r.order)) + 1;
}

function nextBartikOrder(rows: AshtadhyayiBartik[]): number {
  if (rows.length === 0) return 0;
  return Math.max(...rows.map((r) => r.order)) + 1;
}

function nextBartikPadachhedOrder(rows: BartikPadachhed[]): number {
  if (rows.length === 0) return 0;
  return Math.max(...rows.map((r) => r.order)) + 1;
}

type PadachhedLinkLookupProps = {
  barnaRows: Barna[];
  bhedRows: Bhed[];
  pratyaharOptions: Pratyahar[];
  barnaSamuhaOptions: BarnaSamuha[];
  ganaOptions: Gana[];
  lookupLoading: boolean;
};

type PadachhedCrudSectionProps = PadachhedLinkLookupProps & {
  itemKey: string;
  rows: Padachhed[];
  onChanged: () => Promise<void>;
};

type BartikFormValues = {
  order: number;
  text: string;
};

type BartikPadachhedFormValues = {
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  lupta: boolean;
};

type BartikAnubrittiFormValues = {
  order: number;
  text: string;
  link?: number | null;
  ashtadhyayiBartikPadachhedId?: number | null;
};

type BartikAdhikarFormValues = {
  order: number;
  text: string;
  link?: number | null;
};

function PadachhedCrudSection({
  itemKey,
  rows,
  onChanged,
  barnaRows,
  bhedRows,
  pratyaharOptions,
  barnaSamuhaOptions,
  ganaOptions,
  lookupLoading,
}: PadachhedCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Padachhed | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<PadachhedFormValues>();
  const pendingPadachhedFormRef = useRef<PadachhedFormValues | null>(null);

  const applyPendingPadachhedForm = useCallback(() => {
    const values = pendingPadachhedFormRef.current;
    if (!values) return;
    form.setFieldsValue(values);
  }, [form]);

  const openCreate = () => {
    pendingPadachhedFormRef.current = {
      order: nextPadachhedOrder(rows),
      text: '',
      bibhakti: 1,
      bachan: 1,
      lupta: false,
    };
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: Padachhed, e: React.MouseEvent) => {
    e.stopPropagation();
    pendingPadachhedFormRef.current = padachhedToFormValues(row);
    setEditing(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    pendingPadachhedFormRef.current = null;
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deletePadachhed(id);
      message.success('Padachhed deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submitPadachhed = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const digits = values.lupta ? '' : null;
      if (editing) {
        const patch: UpdatePadachhedBody = {};
        const prev = padachhedToFormValues(editing);
        if (values.order !== prev.order) patch.order = values.order;
        if (values.text.trim() !== prev.text) patch.text = values.text.trim();
        if (values.bibhakti !== prev.bibhakti) patch.bibhakti = values.bibhakti;
        if (values.bachan !== prev.bachan) patch.bachan = values.bachan;
        const prevDigits = editing.digits != null ? '' : null;
        if (digits !== prevDigits) patch.digits = digits;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updatePadachhed(editing.id, patch);
        message.success('Padachhed updated');
      } else {
        await createPadachhed({
          i: itemKey,
          order: values.order,
          text: values.text.trim(),
          bibhakti: values.bibhakti,
          bachan: values.bachan,
          digits,
        });
        message.success('Padachhed created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Padachhed> = [
    { title: '#', dataIndex: 'order', width: 48 },
    { title: 'Text', dataIndex: 'text' },
    { title: 'Bibhakti', dataIndex: 'bibhakti', width: 88 },
    { title: 'Bachan', dataIndex: 'bachan', width: 80 },
    {
      title: 'Meta',
      dataIndex: 'digits',
      width: 120,
      render: (d: string | null) => (d != null ? 'लुप्तविभक्तिकम्' : '—'),
    },
    {
      title: 'Pratyahar',
      key: 'pratyaharLinks',
      width: 88,
      render: (_, record) => record.pratyaharLinks.length,
    },
    {
      title: 'Barna samuha',
      key: 'barnaSamuhaLinks',
      width: 100,
      render: (_, record) => record.barnaSamuhaLinks.length,
    },
    {
      title: 'Gana',
      key: 'ganaLinks',
      width: 72,
      render: (_, record) => record.ganaLinks.length,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(record, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this padachhed?" onConfirm={() => void onDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong>Pādadachhed</Typography.Text>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add padachhed
        </Button>
      </Space>
      <Table<Padachhed>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: 'No padachhed rows' }}
        style={{ marginBottom: 0 }}
        expandable={{
          expandedRowRender: (record) => (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Pratyahar links
                </Typography.Text>
                <PadachhedPratyaharLinksPanel
                  padachhedId={record.id}
                  padachhedText={record.text}
                  links={record.pratyaharLinks}
                  onChanged={onChanged}
                  barnaRows={barnaRows}
                  bhedRows={bhedRows}
                  lookupLoading={lookupLoading}
                  pratyaharOptions={pratyaharOptions}
                />
              </div>
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Barna samuha links
                </Typography.Text>
                <PadachhedBarnaSamuhaLinksPanel
                  padachhedId={record.id}
                  padachhedText={record.text}
                  links={record.barnaSamuhaLinks}
                  onChanged={onChanged}
                  barnaRows={barnaRows}
                  bhedRows={bhedRows}
                  lookupLoading={lookupLoading}
                  barnaSamuhaOptions={barnaSamuhaOptions}
                />
              </div>
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Gana links
                </Typography.Text>
                <PadachhedGanaLinksPanel
                  padachhedId={record.id}
                  padachhedText={record.text}
                  links={record.ganaLinks}
                  onChanged={onChanged}
                  ganaOptions={ganaOptions}
                  lookupLoading={lookupLoading}
                />
              </div>
            </Space>
          ),
          rowExpandable: () => true,
        }}
      />
      <Modal
        title={editing ? `Edit padachhed #${editing.id}` : 'Add padachhed'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitPadachhed()}
        confirmLoading={saving}
        destroyOnClose
        width={480}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => applyPendingPadachhedForm(), 0);
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="bibhakti" label="Bibhakti" rules={[{ required: true, message: 'Bibhakti is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="bachan" label="Bachan" rules={[{ required: true, message: 'Bachan is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="lupta" label="लुप्तविभक्तिकम्" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

type BartikPadachhedCrudSectionProps = PadachhedLinkLookupProps & {
  bartikId: number;
  rows: BartikPadachhed[];
  onChanged: () => Promise<void>;
};

function BartikPadachhedCrudSection({
  bartikId,
  rows,
  onChanged,
  barnaRows,
  bhedRows,
  pratyaharOptions,
  barnaSamuhaOptions,
  ganaOptions,
  lookupLoading,
}: BartikPadachhedCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BartikPadachhed | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<BartikPadachhedFormValues>();

  const openCreate = () => {
    form.setFieldsValue({ order: nextBartikPadachhedOrder(rows), text: '', bibhakti: 1, bachan: 1, lupta: false });
    setEditing(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const digits = values.lupta ? '' : null;
      setSaving(true);
      if (editing) {
        const patch: UpdateBartikPadachhedBody = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (values.text.trim() !== editing.text) patch.text = values.text.trim();
        if (values.bibhakti !== editing.bibhakti) patch.bibhakti = values.bibhakti;
        if (values.bachan !== editing.bachan) patch.bachan = values.bachan;
        const prevDigits = editing.digits != null ? '' : null;
        if (digits !== prevDigits) patch.digits = digits;
        if (Object.keys(patch).length > 0) await updateBartikPadachhed(editing.id, patch);
      } else {
        await createBartikPadachhed({
          ashtadhyayiBartikId: bartikId,
          order: values.order,
          text: values.text.trim(),
          bibhakti: values.bibhakti,
          bachan: values.bachan,
          digits,
        });
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<BartikPadachhed> = [
    { title: '#', dataIndex: 'order', width: 48 },
    { title: 'Text', dataIndex: 'text' },
    { title: 'Bibhakti', dataIndex: 'bibhakti', width: 88 },
    { title: 'Bachan', dataIndex: 'bachan', width: 80 },
    { title: 'Pratyahar', width: 88, render: (_, r) => r.pratyaharLinks.length },
    { title: 'Barna samuha', width: 100, render: (_, r) => r.barnaSamuhaLinks.length },
    { title: 'Gana', width: 72, render: (_, r) => r.ganaLinks.length },
    {
      title: 'Actions',
      width: 120,
      render: (_, r) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue({ order: r.order, text: r.text, bibhakti: r.bibhakti, bachan: r.bachan, lupta: r.digits != null }); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete this bartik padachhed?" onConfirm={() => void deleteBartikPadachhed(r.id).then(onChanged)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong>Padachhed</Typography.Text>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>Add padachhed</Button>
      </Space>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={rows} locale={{ emptyText: 'No padachhed rows' }} expandable={{ expandedRowRender: (record) => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <BartikPadachhedPratyaharLinksPanel bartikPadachhedId={record.id} links={record.pratyaharLinks} onChanged={onChanged} barnaRows={barnaRows} bhedRows={bhedRows} lookupLoading={lookupLoading} pratyaharOptions={pratyaharOptions} />
          <BartikPadachhedBarnaSamuhaLinksPanel bartikPadachhedId={record.id} links={record.barnaSamuhaLinks} onChanged={onChanged} barnaRows={barnaRows} bhedRows={bhedRows} lookupLoading={lookupLoading} barnaSamuhaOptions={barnaSamuhaOptions} />
          <BartikPadachhedGanaLinksPanel bartikPadachhedId={record.id} links={record.ganaLinks} onChanged={onChanged} ganaOptions={ganaOptions} lookupLoading={lookupLoading} />
        </Space>
      ) }} />
      <Modal title={editing ? `Edit bartik padachhed #${editing.id}` : 'Add bartik padachhed'} open={modalOpen} onCancel={closeModal} onOk={() => void submit()} confirmLoading={saving} destroyOnClose>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="order" label="Order" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="bibhakti" label="Bibhakti" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="bachan" label="Bachan" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="lupta" label="लुप्तविभक्तिकम्" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}

type BartikAnubrittiCrudSectionProps = {
  bartikId: number;
  rows: BartikAnubritti[];
  bartikPadachheds: BartikPadachhed[];
  sutraPickerSeeds: SutraPickerSeed[];
  onChanged: () => Promise<void>;
  onOpenLinkedSutra?: (sutraId: number) => void;
};

function BartikAnubrittiCrudSection({
  bartikId,
  rows,
  bartikPadachheds,
  sutraPickerSeeds,
  onChanged,
  onOpenLinkedSutra,
}: BartikAnubrittiCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BartikAnubritti | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOptions, setPickerOptions] = useState<{ value: number; label: string }[]>([]);
  const [form] = Form.useForm<BartikAnubrittiFormValues>();
  const formDraftRef = useRef<BartikAnubrittiFormValues | null>(null);

  const syncFormFromDraft = useCallback(() => {
    const draft = formDraftRef.current;
    if (draft) form.setFieldsValue(draft);
  }, [form]);

  const seedOptions = useMemo(() => buildSutraPickerOptions(sutraPickerSeeds), [sutraPickerSeeds]);
  const bartikPadachhedOptions = useMemo(
    () => buildBartikPadachhedPickerOptions(bartikPadachheds),
    [bartikPadachheds]
  );

  useEffect(() => {
    if (!modalOpen) return;
    setPickerOptions(seedOptions);
    let cancelled = false;
    setPickerLoading(true);
    void listAshtadhyayiPaginated(1, 200)
      .then((res) => {
        if (cancelled) return;
        setPickerOptions(buildSutraPickerOptions([...sutraPickerSeeds, ...res.data]));
      })
      .catch(() => {
        if (!cancelled) setPickerOptions(seedOptions);
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, seedOptions, sutraPickerSeeds]);

  const openCreate = () => {
    formDraftRef.current = {
      order: rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 0,
      text: '',
      link: undefined,
      ashtadhyayiBartikPadachhedId: undefined,
    };
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: BartikAnubritti, e: React.MouseEvent) => {
    e.stopPropagation();
    formDraftRef.current = {
      order: row.order,
      text: row.text,
      link: row.link ?? undefined,
      ashtadhyayiBartikPadachhedId: row.ashtadhyayiBartikPadachhedId ?? undefined,
    };
    setEditing(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    formDraftRef.current = null;
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deleteBartikAnubritti(id);
      message.success('Anubṛtti deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const text = stripTrailingItemKeyRef(values.text);
      const linkNorm = values.link == null || !Number.isFinite(Number(values.link)) ? null : Number(values.link);
      const padachhedNorm =
        values.ashtadhyayiBartikPadachhedId == null ||
        !Number.isFinite(Number(values.ashtadhyayiBartikPadachhedId))
          ? null
          : Number(values.ashtadhyayiBartikPadachhedId);
      if (editing) {
        const patch: UpdateBartikAnubrittiBody = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (text !== editing.text) patch.text = text;
        if (linkNorm !== editing.link) patch.link = linkNorm;
        if (padachhedNorm !== editing.ashtadhyayiBartikPadachhedId) {
          patch.ashtadhyayiBartikPadachhedId = padachhedNorm;
        }
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateBartikAnubritti(editing.id, patch);
        message.success('Anubṛtti updated');
      } else {
        await createBartikAnubritti({
          ashtadhyayiBartikId: bartikId,
          order: values.order,
          text,
          ...(linkNorm != null ? { link: linkNorm } : {}),
          ...(padachhedNorm != null ? { ashtadhyayiBartikPadachhedId: padachhedNorm } : {}),
        });
        message.success('Anubṛtti created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<BartikAnubritti> = [
    { title: '#', dataIndex: 'order', width: 56 },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Linked bartik padachhed',
      ellipsis: true,
      render: (_, record) => {
        const p = record.ashtadhyayiBartikPadachhed;
        if (!p) {
          return record.ashtadhyayiBartikPadachhedId != null ? `#${record.ashtadhyayiBartikPadachhedId}` : '—';
        }
        return bartikPadachhedPickerLabel(p);
      },
    },
    {
      title: 'Linked sūtra',
      ellipsis: true,
      render: (_, record) => {
        const s = record.linkSutra;
        if (!s) return '—';
        return (
          <Space size={4} wrap>
            <Typography.Text ellipsis style={{ maxWidth: 260 }}>
              {s.sutraText}
            </Typography.Text>
            {onOpenLinkedSutra && record.link != null ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onOpenLinkedSutra(record.link!)}
              >
                Open
              </Button>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      width: 110,
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(record, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this anubṛtti?" onConfirm={() => void onDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <span />
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add anubṛtti
        </Button>
      </Space>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={rows} locale={{ emptyText: 'No anubṛtti rows' }} />
      <Modal
        title={editing ? `Edit anubṛtti #${editing.id}` : 'Add anubṛtti'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submit()}
        confirmLoading={saving}
        destroyOnClose
        width={640}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => syncFormFromDraft(), 0);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="link" label="Linked sūtra">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select sūtra"
              options={pickerOptions}
              loading={pickerLoading}
              notFoundContent={pickerLoading ? 'Loading…' : 'No sūtra found'}
            />
          </Form.Item>
          <Form.Item name="ashtadhyayiBartikPadachhedId" label="Linked bartik padachhed">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select bartik padachhed"
              options={bartikPadachhedOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

type BartikAdhikarCrudSectionProps = {
  bartikId: number;
  rows: BartikAdhikar[];
  sutraPickerSeeds: SutraPickerSeed[];
  onChanged: () => Promise<void>;
  onOpenLinkedSutra?: (sutraId: number) => void;
};

function BartikAdhikarCrudSection({
  bartikId,
  rows,
  sutraPickerSeeds,
  onChanged,
  onOpenLinkedSutra,
}: BartikAdhikarCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BartikAdhikar | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOptions, setPickerOptions] = useState<{ value: number; label: string }[]>([]);
  const [form] = Form.useForm<BartikAdhikarFormValues>();
  const formDraftRef = useRef<BartikAdhikarFormValues | null>(null);

  const syncFormFromDraft = useCallback(() => {
    const draft = formDraftRef.current;
    if (draft) form.setFieldsValue(draft);
  }, [form]);

  const seedOptions = useMemo(() => buildSutraPickerOptions(sutraPickerSeeds), [sutraPickerSeeds]);

  useEffect(() => {
    if (!modalOpen) return;
    setPickerOptions(seedOptions);
    let cancelled = false;
    setPickerLoading(true);
    void listAshtadhyayiPaginated(1, 200)
      .then((res) => {
        if (cancelled) return;
        setPickerOptions(buildSutraPickerOptions([...sutraPickerSeeds, ...res.data]));
      })
      .catch(() => {
        if (!cancelled) setPickerOptions(seedOptions);
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, seedOptions, sutraPickerSeeds]);

  const openCreate = () => {
    formDraftRef.current = {
      order: rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 0,
      text: '',
      link: undefined,
    };
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: BartikAdhikar, e: React.MouseEvent) => {
    e.stopPropagation();
    formDraftRef.current = {
      order: row.order,
      text: row.text,
      link: row.link ?? undefined,
    };
    setEditing(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    formDraftRef.current = null;
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deleteBartikAdhikar(id);
      message.success('Adhikāra deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const text = stripTrailingItemKeyRef(values.text);
      const linkNorm = values.link == null || !Number.isFinite(Number(values.link)) ? null : Number(values.link);
      if (editing) {
        const patch: UpdateBartikAdhikarBody = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (text !== editing.text) patch.text = text;
        if (linkNorm !== editing.link) patch.link = linkNorm;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateBartikAdhikar(editing.id, patch);
        message.success('Adhikāra updated');
      } else {
        await createBartikAdhikar({
          ashtadhyayiBartikId: bartikId,
          order: values.order,
          text,
          ...(linkNorm != null ? { link: linkNorm } : {}),
        });
        message.success('Adhikāra created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<BartikAdhikar> = [
    { title: '#', dataIndex: 'order', width: 56 },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Linked sūtra',
      ellipsis: true,
      render: (_, record) => {
        const s = record.linkSutra;
        if (!s) return record.link != null ? `#${record.link}` : '—';
        return (
          <Space size={4} wrap>
            <Typography.Text ellipsis style={{ maxWidth: 280 }}>
              {s.sutraText || `#${s.id}`}
            </Typography.Text>
            {onOpenLinkedSutra && record.link != null ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onOpenLinkedSutra(record.link!)}
              >
                Open
              </Button>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      width: 110,
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(record, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this adhikāra?" onConfirm={() => void onDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <span />
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add adhikāra
        </Button>
      </Space>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={rows} locale={{ emptyText: 'No adhikāra rows' }} />
      <Modal
        title={editing ? `Edit adhikāra #${editing.id}` : 'Add adhikāra'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submit()}
        confirmLoading={saving}
        destroyOnClose
        width={640}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => syncFormFromDraft(), 0);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="link" label="Linked sūtra">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select sūtra"
              options={pickerOptions}
              loading={pickerLoading}
              notFoundContent={pickerLoading ? 'Loading…' : 'No sūtra found'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

type BartikCrudSectionProps = PadachhedLinkLookupProps & {
  ashtadhyayiSutraId: number;
  rows: AshtadhyayiBartik[];
  sutraPickerSeeds: SutraPickerSeed[];
  onChanged: () => Promise<void>;
  onOpenLinkedSutra?: (sutraId: number) => void;
};

function BartikCrudSection({
  ashtadhyayiSutraId,
  rows,
  sutraPickerSeeds,
  onChanged,
  onOpenLinkedSutra,
  ...lookup
}: BartikCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AshtadhyayiBartik | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<BartikFormValues>();

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const patch: { order?: number; text?: string } = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (values.text.trim() !== editing.text) patch.text = values.text.trim();
        if (Object.keys(patch).length > 0) await updateAshtadhyayiBartik(editing.id, patch);
      } else {
        await createAshtadhyayiBartik({ ashtadhyayiSutraId, order: values.order, text: values.text.trim() });
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<AshtadhyayiBartik> = [
    { title: '#', dataIndex: 'order', width: 48 },
    { title: 'Bartik', dataIndex: 'text' },
    { title: 'Padachhed', width: 90, render: (_, r) => r.padachheds.length },
    { title: 'Anubṛtti', width: 84, render: (_, r) => r.anubrittis.length },
    { title: 'Adhikāra', width: 84, render: (_, r) => r.adhikars.length },
    {
      title: 'Actions',
      width: 120,
      render: (_, r) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue({ order: r.order, text: r.text }); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete this bartik?" onConfirm={() => void deleteAshtadhyayiBartik(r.id).then(onChanged)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong>Bartik</Typography.Text>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.setFieldsValue({ order: nextBartikOrder(rows), text: '' }); setModalOpen(true); }}>Add bartik</Button>
      </Space>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={rows} locale={{ emptyText: 'No bartik rows' }} expandable={{ expandedRowRender: (record) => (
        <Collapse
          defaultActiveKey={['pad']}
          items={[
            {
              key: 'pad',
              label: 'Padachhed',
              children: (
                <BartikPadachhedCrudSection bartikId={record.id} rows={record.padachheds} onChanged={onChanged} {...lookup} />
              ),
            },
            {
              key: 'an',
              label: 'Anubṛtti',
              children: (
                <BartikAnubrittiCrudSection
                  bartikId={record.id}
                  rows={record.anubrittis}
                  bartikPadachheds={record.padachheds}
                  sutraPickerSeeds={sutraPickerSeeds}
                  onChanged={onChanged}
                  onOpenLinkedSutra={onOpenLinkedSutra}
                />
              ),
            },
            {
              key: 'ad',
              label: 'Adhikāra',
              children: (
                <BartikAdhikarCrudSection
                  bartikId={record.id}
                  rows={record.adhikars}
                  sutraPickerSeeds={sutraPickerSeeds}
                  onChanged={onChanged}
                  onOpenLinkedSutra={onOpenLinkedSutra}
                />
              ),
            },
          ]}
        />
      ) }} />
      <Modal title={editing ? `Edit bartik #${editing.id}` : 'Add bartik'} open={modalOpen} onCancel={closeModal} onOk={() => void submit()} confirmLoading={saving} destroyOnClose>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="order" label="Order" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}

type SutraCommentaryTextFormValues = {
  text: string;
  sutraCommentaryId: number;
  languageId: number;
};

type SutraCommentaryTextCrudSectionProps = {
  sutraId: number;
  rows: SutraCommentaryText[];
  commentaryOptions: SutraCommentary[];
  languageOptions: Language[];
  lookupLoading: boolean;
  onChanged: () => Promise<void>;
};

function SutraCommentaryTextCrudSection({
  sutraId,
  rows,
  commentaryOptions,
  languageOptions,
  lookupLoading,
  onChanged,
}: SutraCommentaryTextCrudSectionProps) {
  const [localRows, setLocalRows] = useState<SutraCommentaryText[]>(rows);
  const [commentaryFilterId, setCommentaryFilterId] = useState<number | null>(null);
  const [languageFilterId, setLanguageFilterId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SutraCommentaryText | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<SutraCommentaryTextFormValues>();
  const formDraftRef = useRef<SutraCommentaryTextFormValues | null>(null);

  const loadRows = useCallback(async () => {
    try {
      const fetched = await listSutraCommentaryText(sutraId);
      setLocalRows(fetched.filter((r) => r.sutraId === sutraId));
    } catch {
      setLocalRows(rows.filter((r) => r.sutraId === sutraId));
    }
  }, [sutraId, rows]);

  useEffect(() => {
    setLocalRows(rows.filter((r) => r.sutraId === sutraId));
  }, [rows, sutraId]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const syncFormFromDraft = useCallback(() => {
    const draft = formDraftRef.current;
    if (draft) form.setFieldsValue(draft);
  }, [form]);

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    formDraftRef.current = null;
    form.resetFields();
  };

  const openCreate = () => {
    formDraftRef.current = null;
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: SutraCommentaryText, e: React.MouseEvent) => {
    e.stopPropagation();
    formDraftRef.current = {
      text: row.text,
      sutraCommentaryId: row.sutraCommentaryId,
      languageId: row.languageId,
    };
    setEditing(row);
    setModalOpen(true);
  };

  const onDelete = async (id: number) => {
    try {
      await deleteSutraCommentaryText(id);
      message.success('Commentary text deleted');
      await onChanged();
      await loadRows();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const text = values.text.trim();
      setSaving(true);
      if (editing) {
        const patch: UpdateSutraCommentaryTextBody = {};
        if (text !== editing.text) patch.text = text;
        if (values.sutraCommentaryId !== editing.sutraCommentaryId) {
          patch.sutraCommentaryId = values.sutraCommentaryId;
        }
        if (values.languageId !== editing.languageId) patch.languageId = values.languageId;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateSutraCommentaryText(editing.id, patch);
        message.success('Commentary text updated');
      } else {
        await createSutraCommentaryText({
          sutraId,
          text,
          sutraCommentaryId: values.sutraCommentaryId,
          languageId: values.languageId,
        });
        message.success('Commentary text created');
      }
      closeModal();
      await onChanged();
      await loadRows();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<SutraCommentaryText> = [
    {
      title: 'Commentary',
      width: 180,
      render: (_, row) =>
        row.sutraCommentary?.name ??
        commentaryOptions.find((x) => x.id === row.sutraCommentaryId)?.name ??
        `#${row.sutraCommentaryId}`,
    },
    {
      title: 'Language',
      width: 140,
      render: (_, row) =>
        row.language?.name ??
        languageOptions.find((x) => x.id === row.languageId)?.name ??
        `#${row.languageId}`,
    },
    { title: 'Text', dataIndex: 'text' },
    {
      title: 'Actions',
      width: 120,
      render: (_, row) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(row, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this commentary text?" onConfirm={() => void onDelete(row.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredRows = useMemo(
    () =>
      localRows.filter((row) => {
        if (commentaryFilterId != null && row.sutraCommentaryId !== commentaryFilterId) return false;
        if (languageFilterId != null && row.languageId !== languageFilterId) return false;
        return true;
      }),
    [localRows, commentaryFilterId, languageFilterId]
  );

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate} disabled={lookupLoading}>
          Add commentary text
        </Button>
      </Space>
      <Space wrap style={{ marginBottom: 8 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          placeholder="Filter by commentary"
          value={commentaryFilterId ?? undefined}
          onChange={(v) => setCommentaryFilterId(v ?? null)}
          options={commentaryOptions.map((x) => ({ value: x.id, label: `${x.name} (#${x.id})` }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          placeholder="Filter by language"
          value={languageFilterId ?? undefined}
          onChange={(v) => setLanguageFilterId(v ?? null)}
          options={languageOptions.map((x) => ({ value: x.id, label: `${x.name} (${x.code})` }))}
        />
      </Space>
      <Table<SutraCommentaryText>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={filteredRows}
        locale={{ emptyText: 'No commentary texts' }}
      />
      <Modal
        title={editing ? `Edit commentary text #${editing.id}` : 'Add commentary text'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submit()}
        confirmLoading={saving}
        destroyOnClose
        width={720}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => syncFormFromDraft(), 0);
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="sutraCommentaryId" label="Sutra commentary" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select commentary source"
              options={commentaryOptions.map((x) => ({ value: x.id, label: `${x.name} (#${x.id})` }))}
              loading={lookupLoading}
            />
          </Form.Item>
          <Form.Item name="languageId" label="Language" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
              options={languageOptions.map((x) => ({ value: x.id, label: `${x.name} (${x.code})` }))}
              loading={lookupLoading}
            />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea rows={5} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function nextAnubrittiOrder(rows: Anubritti[]): number {
  if (rows.length === 0) return 0;
  return Math.max(...rows.map((r) => r.order)) + 1;
}

function stripTrailingItemKeyRef(text: string): string {
  return text.replace(/\$\d+\s*$/, '').trim();
}

type AnubrittiFormValues = {
  order: number;
  text: string;
  link?: number | null;
  ashtadhyayiPadachhedId?: number | null;
};

type AnubrittiCrudSectionProps = {
  ashtadhyayiSutraId: number;
  rows: Anubritti[];
  sutraPickerSeeds: SutraPickerSeed[];
  padachhedPickerSeeds: AnubrittiPadachhedRef[];
  onChanged: () => Promise<void>;
  onOpenLinkedSutra?: (sutraId: number) => void;
};

function AnubrittiCrudSection({
  ashtadhyayiSutraId,
  rows,
  sutraPickerSeeds,
  padachhedPickerSeeds,
  onChanged,
  onOpenLinkedSutra,
}: AnubrittiCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Anubritti | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOptions, setPickerOptions] = useState<{ value: number; label: string }[]>([]);
  const [padachhedLoading, setPadachhedLoading] = useState(false);
  const [padachhedOptions, setPadachhedOptions] = useState<{ value: number; label: string }[]>([]);
  const [form] = Form.useForm<AnubrittiFormValues>();
  const formDraftRef = useRef<AnubrittiFormValues | null>(null);

  const watchedLink = Form.useWatch('link', form);

  const syncFormFromDraft = useCallback(() => {
    const draft = formDraftRef.current;
    if (draft) form.setFieldsValue(draft);
  }, [form]);

  const seedOptions = useMemo(
    () => buildSutraPickerOptions(sutraPickerSeeds),
    [sutraPickerSeeds]
  );

  useEffect(() => {
    if (!modalOpen) return;
    setPickerOptions(seedOptions);
    let cancelled = false;
    setPickerLoading(true);
    void listAshtadhyayiPaginated(1, 200)
      .then((res) => {
        if (cancelled) return;
        setPickerOptions(buildSutraPickerOptions([...sutraPickerSeeds, ...res.data]));
      })
      .catch(() => {
        if (!cancelled) setPickerOptions(seedOptions);
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, seedOptions, sutraPickerSeeds]);

  const linkSelectOptions = useMemo(
    () => pickerOptions.filter((o) => o.value !== ashtadhyayiSutraId),
    [pickerOptions, ashtadhyayiSutraId]
  );

  const currentSutraPadachhedOptions = useMemo(
    () =>
      buildPadachhedPickerOptions(
        padachhedPickerSeeds.filter((p) => p.ashtadhyayiSutraId === ashtadhyayiSutraId)
      ),
    [padachhedPickerSeeds, ashtadhyayiSutraId]
  );

  useEffect(() => {
    if (!modalOpen) return;
    const linkId =
      watchedLink == null || !Number.isFinite(Number(watchedLink)) ? null : Number(watchedLink);
    if (linkId == null) {
      setPadachhedOptions(currentSutraPadachhedOptions);
      return;
    }
    let cancelled = false;
    setPadachhedLoading(true);
    void getAshtadhyayi(linkId)
      .then((s) => {
        if (cancelled) return;
        setPadachhedOptions(buildPadachhedPickerOptions(s.padachheds.map(padachhedToRef)));
      })
      .catch(() => {
        if (!cancelled) setPadachhedOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPadachhedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, watchedLink, currentSutraPadachhedOptions]);

  const openCreate = () => {
    formDraftRef.current = {
      order: nextAnubrittiOrder(rows),
      text: '',
      link: undefined,
      ashtadhyayiPadachhedId: undefined,
    };
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: Anubritti, e: React.MouseEvent) => {
    e.stopPropagation();
    formDraftRef.current = {
      order: row.order,
      text: row.text,
      link: row.link ?? undefined,
      ashtadhyayiPadachhedId: row.ashtadhyayiPadachhedId ?? undefined,
    };
    setEditing(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    formDraftRef.current = null;
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deleteAnubritti(id);
      message.success('Anubṛtti deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submitAnubritti = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const text = stripTrailingItemKeyRef(values.text);
      const linkNorm =
        values.link == null || !Number.isFinite(Number(values.link)) ? null : Number(values.link);
      const padachhedNorm =
        values.ashtadhyayiPadachhedId == null || !Number.isFinite(Number(values.ashtadhyayiPadachhedId))
          ? null
          : Number(values.ashtadhyayiPadachhedId);
      if (editing) {
        const patch: UpdateAnubrittiBody = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (text !== editing.text) patch.text = text;
        if (linkNorm !== editing.link) patch.link = linkNorm;
        if (padachhedNorm !== editing.ashtadhyayiPadachhedId) {
          patch.ashtadhyayiPadachhedId = padachhedNorm;
        }
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateAnubritti(editing.id, patch);
        message.success('Anubṛtti updated');
      } else {
        await createAnubritti({
          ashtadhyayiSutraId,
          order: values.order,
          text,
          ...(linkNorm != null ? { link: linkNorm } : {}),
          ...(padachhedNorm != null ? { ashtadhyayiPadachhedId: padachhedNorm } : {}),
        });
        message.success('Anubṛtti created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Anubritti> = [
    { title: '#', dataIndex: 'order', width: 56 },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Linked padachhed',
      key: 'padachhed',
      ellipsis: true,
      render: (_, record) => {
        const p = record.ashtadhyayiPadachhed;
        if (!p) return record.ashtadhyayiPadachhedId != null ? `#${record.ashtadhyayiPadachhedId}` : '—';
        return padachhedPickerLabel(p);
      },
    },
    {
      title: 'Linked sūtra',
      key: 'linkSutra',
      ellipsis: true,
      render: (_, record) => {
        const s = record.linkSutra;
        if (!s) return record.link != null ? '—' : '—';
        return (
          <Space size={4} wrap>
            <Typography.Text ellipsis style={{ maxWidth: 280 }}>
              {s.sutraText}
            </Typography.Text>
            {onOpenLinkedSutra && record.link != null ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onOpenLinkedSutra(record.link!)}
              >
                Open
              </Button>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(record, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this anubṛtti?" onConfirm={() => void onDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <span />
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add anubṛtti
        </Button>
      </Space>
      <Table<Anubritti>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: 'No anubṛtti rows' }}
        style={{ marginBottom: 0 }}
      />
      <Modal
        title={editing ? `Edit anubṛtti #${editing.id}` : 'Add anubṛtti'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitAnubritti()}
        confirmLoading={saving}
        destroyOnClose
        width={640}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => syncFormFromDraft(), 0);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="link"
            label="Linked sūtra"
            tooltip="Sūtra this anubṛtti points to (loads padachhed options for that sūtra)"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select sūtra"
              options={linkSelectOptions}
              loading={pickerLoading}
              notFoundContent={pickerLoading ? 'Loading…' : 'No sūtra found'}
              onChange={() => form.setFieldValue('ashtadhyayiPadachhedId', undefined)}
            />
          </Form.Item>
          <Form.Item
            name="ashtadhyayiPadachhedId"
            label="Linked padachhed"
            tooltip="Padachhed on the linked sūtra (or this sūtra if no link is set)"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={watchedLink ? 'Select padachhed on linked sūtra' : 'Select padachhed'}
              options={padachhedOptions}
              loading={padachhedLoading}
              notFoundContent={padachhedLoading ? 'Loading…' : 'No padachhed found'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function nextAdhikarOrder(rows: Adhikar[]): number {
  if (rows.length === 0) return 0;
  return Math.max(...rows.map((r) => r.order)) + 1;
}

type AdhikarFormValues = {
  order: number;
  text: string;
  link?: number | null;
};

type AdhikarCrudSectionProps = {
  ashtadhyayiSutraId: number;
  rows: Adhikar[];
  sutraPickerSeeds: SutraPickerSeed[];
  onChanged: () => Promise<void>;
  onOpenLinkedSutra?: (sutraId: number) => void;
};

function AdhikarCrudSection({
  ashtadhyayiSutraId,
  rows,
  sutraPickerSeeds,
  onChanged,
  onOpenLinkedSutra,
}: AdhikarCrudSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Adhikar | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOptions, setPickerOptions] = useState<{ value: number; label: string }[]>([]);
  const [form] = Form.useForm<AdhikarFormValues>();
  const formDraftRef = useRef<AdhikarFormValues | null>(null);

  const syncFormFromDraft = useCallback(() => {
    const draft = formDraftRef.current;
    if (draft) form.setFieldsValue(draft);
  }, [form]);

  const seedOptions = useMemo(
    () => buildSutraPickerOptions(sutraPickerSeeds),
    [sutraPickerSeeds]
  );

  useEffect(() => {
    if (!modalOpen) return;
    setPickerOptions(seedOptions);
    let cancelled = false;
    setPickerLoading(true);
    void listAshtadhyayiPaginated(1, 200)
      .then((res) => {
        if (cancelled) return;
        setPickerOptions(buildSutraPickerOptions([...sutraPickerSeeds, ...res.data]));
      })
      .catch(() => {
        if (!cancelled) setPickerOptions(seedOptions);
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, seedOptions, sutraPickerSeeds]);

  const linkSelectOptions = useMemo(
    () => pickerOptions.filter((o) => o.value !== ashtadhyayiSutraId),
    [pickerOptions, ashtadhyayiSutraId]
  );

  const openCreate = () => {
    formDraftRef.current = {
      order: nextAdhikarOrder(rows),
      text: '',
      link: undefined,
    };
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: Adhikar, e: React.MouseEvent) => {
    e.stopPropagation();
    formDraftRef.current = {
      order: row.order,
      text: row.text,
      link: row.link ?? undefined,
    };
    setEditing(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    formDraftRef.current = null;
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deleteAdhikar(id);
      message.success('Adhikāra deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submitAdhikar = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const text = stripTrailingItemKeyRef(values.text);
      const linkNorm =
        values.link == null || !Number.isFinite(Number(values.link)) ? null : Number(values.link);
      if (editing) {
        const patch: UpdateAdhikarBody = {};
        if (values.order !== editing.order) patch.order = values.order;
        if (text !== editing.text) patch.text = text;
        if (linkNorm !== editing.link) patch.link = linkNorm;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateAdhikar(editing.id, patch);
        message.success('Adhikāra updated');
      } else {
        await createAdhikar({
          ashtadhyayiSutraId,
          order: values.order,
          text,
          ...(linkNorm != null ? { link: linkNorm } : {}),
        });
        message.success('Adhikāra created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Adhikar> = [
    { title: '#', dataIndex: 'order', width: 56 },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Linked sūtra',
      key: 'linkSutra',
      ellipsis: true,
      render: (_, record) => {
        const s = record.linkSutra;
        if (!s) return record.link != null ? `#${record.link}` : '—';
        return (
          <Space size={4} wrap>
            <Typography.Text ellipsis style={{ maxWidth: 280 }}>
              {s.sutraText || `#${s.id}`}
            </Typography.Text>
            {onOpenLinkedSutra && record.link != null ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onOpenLinkedSutra(record.link!)}
              >
                Open
              </Button>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => openEdit(record, e)}>
            Edit
          </Button>
          <Popconfirm title="Delete this adhikāra?" onConfirm={() => void onDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <span />
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add adhikāra
        </Button>
      </Space>
      <Table<Adhikar>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: 'No adhikāra rows' }}
        style={{ marginBottom: 0 }}
      />
      <Modal
        title={editing ? `Edit adhikāra #${editing.id}` : 'Add adhikāra'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitAdhikar()}
        confirmLoading={saving}
        destroyOnClose
        width={640}
        afterOpenChange={(open) => {
          if (!open) return;
          window.setTimeout(() => syncFormFromDraft(), 0);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="link"
            label="Linked sūtra"
            tooltip="Sūtra this adhikāra points to (the rule that introduced this adhikāra)"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select sūtra"
              options={linkSelectOptions}
              loading={pickerLoading}
              notFoundContent={pickerLoading ? 'Loading…' : 'No sūtra found'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function SutraFormFields() {
  const long = { minRows: 2, maxRows: 12 } as const;
  return (
    <Collapse
      defaultActiveKey={['core']}
      items={[
        {
          key: 'core',
          label: 'Identifiers & primary text',
          children: (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Form.Item name="itemKey" label="itemKey (stable id)" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Space wrap style={{ width: '100%' }}>
                <Form.Item name="a" label="ā" rules={[{ required: true }]} style={{ minWidth: 72, marginBottom: 0 }}>
                  <Input />
                </Form.Item>
                <Form.Item name="p" label="p" rules={[{ required: true }]} style={{ minWidth: 72, marginBottom: 0 }}>
                  <Input />
                </Form.Item>
                <Form.Item name="n" label="n" rules={[{ required: true }]} style={{ minWidth: 72, marginBottom: 0 }}>
                  <Input />
                </Form.Item>
              </Space>
              <Form.Item name="sutraText" label="Sūtra (Devanagarī)" rules={[{ required: true }]}>
                <Input.TextArea autoSize={long} />
              </Form.Item>
              <Form.Item name="transliteration" label="Transliteration" rules={[{ required: true }]}>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="ss" label="Summary (ss)" rules={[{ required: true }]}>
                <Input.TextArea autoSize={long} />
              </Form.Item>
              <Form.Item name="padachhed" label="Pādadachhed (padachhed)" rules={[{ required: true }]}>
                <Input.TextArea autoSize={long} />
              </Form.Item>
            </Space>
          ),
        },
        {
          key: 'long',
          label: 'Commentary (an / ad)',
          children: (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Form.Item name="an" label="an" rules={[{ required: true }]}>
                <Input.TextArea autoSize={long} placeholder="May contain ## separators" />
              </Form.Item>
              <Form.Item name="ad" label="ad" rules={[{ required: true }]}>
                <Input.TextArea autoSize={long} placeholder="May contain ## separators" />
              </Form.Item>
            </Space>
          ),
        },
        {
          key: 'meta',
          label: 'Numeric & chapter metadata',
          children: (
            <Space direction="vertical" style={{ width: '100%' }} wrap>
              {(
                [
                  ['skn', 'skn'],
                  ['lskn', 'lskn'],
                  ['mskn', 'mskn'],
                  ['sskn', 'sskn'],
                  ['plskn', 'plskn'],
                  ['lpn', 'lpn'],
                  ['skChapter', 'skChapter'],
                  ['lskChapter', 'lskChapter'],
                  ['rpn', 'rpn'],
                  ['psn', 'psn'],
                  ['psChapter', 'psChapter'],
                ] as const
              ).map(([name, label]) => (
                <Form.Item key={name} name={name} label={label} rules={[{ required: true }]} style={{ minWidth: 200 }}>
                  <Input />
                </Form.Item>
              ))}
            </Space>
          ),
        },
      ]}
    />
  );
}

export function AshtadhyayiPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [rows, setRows] = useState<AshtadhyayiSutra[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<AshtadhyayiSutra | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AshtadhyayiSutra | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<CreateAshtadhyayiBody>();

  const [barnaRows, setBarnaRows] = useState<Barna[]>([]);
  const [bhedRows, setBhedRows] = useState<Bhed[]>([]);
  const [pratyaharOptions, setPratyaharOptions] = useState<Pratyahar[]>([]);
  const [barnaSamuhaOptions, setBarnaSamuhaOptions] = useState<BarnaSamuha[]>([]);
  const [ganaOptions, setGanaOptions] = useState<Gana[]>([]);
  const [sutraCommentaryOptions, setSutraCommentaryOptions] = useState<SutraCommentary[]>([]);
  const [languageOptions, setLanguageOptions] = useState<Language[]>([]);
  const [linkLookupLoading, setLinkLookupLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLinkLookupLoading(true);
    void Promise.all([
      listBarna(),
      listBhed(),
      listPratyahar(),
      listBarnaSamuha(),
      listGana(),
      listSutraCommentary(),
      listLanguages(),
    ]).then(([barnas, bheds, pratyahars, barnaSamuhas, ganas, sutraCommentaries, languages]) => {
        if (cancelled) return;
        setBarnaRows(barnas);
        setBhedRows(bheds);
        setPratyaharOptions(pratyahars);
        setBarnaSamuhaOptions(barnaSamuhas);
        setGanaOptions(ganas);
        setSutraCommentaryOptions(sutraCommentaries);
        setLanguageOptions(languages);
      })
      .catch((err: unknown) => {
        if (!cancelled) message.error(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLinkLookupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAshtadhyayiPaginated(page, limit);
      setRows(res.data);
      setTotal(res.pagination.total);
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to load Ashtadhyayi');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      if (r.itemKey.toLowerCase().includes(q)) return true;
      if (r.sutraText.toLowerCase().includes(q)) return true;
      if (r.transliteration.toLowerCase().includes(q)) return true;
      if (locationLabel(r).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [rows, filter]);

  const linkedSutraPickerSeeds = useMemo((): SutraPickerSeed[] => {
    const seeds: SutraPickerSeed[] = [...rows];
    if (detail) {
      seeds.push(detail);
      const pushLinked = (link: number | null, linkSutra: SutraPickerSeed | null) => {
        if (linkSutra) seeds.push(linkSutra);
        else if (link != null) {
          seeds.push({
            id: link,
            itemKey: `Sūtra #${link}`,
            sutraText: '',
            a: '',
            p: '',
            n: '',
          });
        }
      };
      for (const a of detail.anubrittis) pushLinked(a.link, a.linkSutra);
      for (const ad of detail.adhikars) pushLinked(ad.link, ad.linkSutra);
    }
    return seeds;
  }, [rows, detail]);

  const anubrittiPadachhedPickerSeeds = useMemo((): AnubrittiPadachhedRef[] => {
    if (!detail) return [];
    const byId = new Map<number, AnubrittiPadachhedRef>();
    for (const p of detail.padachheds) {
      byId.set(p.id, padachhedToRef(p));
    }
    for (const a of detail.anubrittis) {
      if (a.ashtadhyayiPadachhed) byId.set(a.ashtadhyayiPadachhed.id, a.ashtadhyayiPadachhed);
    }
    return Array.from(byId.values());
  }, [detail]);

  const openDetail = async (id: number) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    try {
      const row = await getAshtadhyayi(id);
      setDetail(row);
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to load sūtra');
      setDetail(null);
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetail(null);
  };

  const reloadDetail = useCallback(async () => {
    if (!detail) return;
    setDetailLoading(true);
    try {
      setDetail(await getAshtadhyayi(detail.id));
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to refresh sūtra');
    } finally {
      setDetailLoading(false);
    }
  }, [detail]);

  const detailNavIndex = useMemo(
    () => (detail ? filteredRows.findIndex((r) => r.id === detail.id) : -1),
    [detail, filteredRows]
  );
  const canPrev = detailNavIndex > 0;
  const canNext = detailNavIndex >= 0 && detailNavIndex < filteredRows.length - 1;

  const goPrevSutra = () => {
    if (!canPrev) return;
    void openDetail(filteredRows[detailNavIndex - 1].id);
  };

  const goNextSutra = () => {
    if (!canNext) return;
    void openDetail(filteredRows[detailNavIndex + 1].id);
  };

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue(emptyCreateAshtadhyayiBody());
    setEditOpen(true);
  };

  const openEditFromRow = (record: AshtadhyayiSutra, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(record);
    form.setFieldsValue(sutraToFormValues(record));
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submitForm = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const editedId = editing?.id ?? null;
      if (editing) {
        const patch: UpdateAshtadhyayiBody = {};
        const prev = sutraToFormValues(editing);
        (Object.keys(prev) as (keyof CreateAshtadhyayiBody)[]).forEach((k) => {
          if (values[k] !== prev[k]) patch[k] = values[k];
        });
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeEdit();
          return;
        }
        await updateAshtadhyayi(editing.id, patch);
        message.success('Updated');
      } else {
        await createAshtadhyayi(values);
        message.success('Created');
      }
      closeEdit();
      await load();
      if (detail && editedId != null && detail.id === editedId) {
        try {
          setDetail(await getAshtadhyayi(editedId));
        } catch {
          /* ignore */
        }
      }
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deleteAshtadhyayi(id);
      message.success('Deleted');
      closeDetailModal();
      await load();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const columns: ColumnsType<AshtadhyayiSutra> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'itemKey', dataIndex: 'itemKey', width: 100, ellipsis: true },
    {
      title: 'Location (a-p-n)',
      key: 'loc',
      width: 120,
      render: (_, r) => locationLabel(r),
    },
    {
      title: 'Sūtra',
      dataIndex: 'sutraText',
      ellipsis: true,
      render: (t: string) => (
        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{t}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 88,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={(e) => openEditFromRow(record, e)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="Ashtadhyayi"
      extra={
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New sūtra
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size="middle">
        <Input.Search
          allowClear
          placeholder="Filter current page by itemKey, sūtra, transliteration, location…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Space>
      <Table<AshtadhyayiSutra>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredRows}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (t) => `${t} sūtras`,
          onChange: (p, ps) => {
            if (ps !== limit) {
              setLimit(ps);
              setPage(1);
              return;
            }
            setPage(p);
          },
        }}
        scroll={{ x: 800 }}
        onRow={(record) => ({
          onClick: () => void openDetail(record.id),
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={detail ? `Sūtra #${detail.id}` : 'Sūtra'}
        open={detailModalOpen}
        onCancel={closeDetailModal}
        width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 24 : 720)}
        footer={
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Button icon={<LeftOutlined />} disabled={!canPrev || detailLoading} onClick={() => goPrevSutra()}>
              Previous
            </Button>
            <Typography.Text type="secondary" style={{ flex: '1 1 auto', textAlign: 'center', minWidth: 120 }}>
              {detailNavIndex >= 0 && filteredRows.length > 0
                ? `${detailNavIndex + 1} of ${filteredRows.length} (this page)`
                : detail
                  ? '—'
                  : ''}
            </Typography.Text>
            <Button icon={<RightOutlined />} disabled={!canNext || detailLoading} onClick={() => goNextSutra()}>
              Next
            </Button>
            <div style={{ flex: '1 1 120px' }} />
            {detail ? (
              <Popconfirm
                title="Delete this sūtra?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => void onDelete(detail.id)}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            ) : null}
            <Button onClick={closeDetailModal}>Close</Button>
          </div>
        }
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        {!detail && detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space
            direction="vertical"
            style={{ width: '100%', opacity: detailLoading ? 0.65 : 1 }}
            size="large"
          >
            {detailLoading ? (
              <Typography.Text type="secondary">Refreshing…</Typography.Text>
            ) : null}
            <div>
              <Typography.Text type="secondary">itemKey</Typography.Text>
              <Typography.Paragraph style={{ marginBottom: 8 }} copyable>
                {detail.itemKey}
              </Typography.Paragraph>
              <Typography.Text type="secondary">Location (ā–p–n)</Typography.Text>
              <Typography.Paragraph style={{ marginBottom: 8 }}>{locationLabel(detail)}</Typography.Paragraph>
              <Typography.Title level={4} style={{ marginTop: 16, fontFamily: 'inherit' }}>
                {detail.sutraText}
              </Typography.Title>
              {detail.transliteration ? (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {detail.transliteration}
                </Typography.Paragraph>
              ) : null}
            </div>
            {detail.ss ? (
              <div>
                <Typography.Text strong>Summary (ss)</Typography.Text>
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{detail.ss}</Typography.Paragraph>
              </div>
            ) : null}
            <Collapse
              items={[
                {
                  key: 'pad',
                  label: 'Pādadachhed & type',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong>Type</Typography.Text>
                      <Typography.Paragraph style={{ marginBottom: 8 }}>{detail.type || '—'}</Typography.Paragraph>
                      <PadachhedCrudSection
                        itemKey={detail.itemKey}
                        rows={detail.padachheds}
                        onChanged={reloadDetail}
                        barnaRows={barnaRows}
                        bhedRows={bhedRows}
                        pratyaharOptions={pratyaharOptions}
                        barnaSamuhaOptions={barnaSamuhaOptions}
                        ganaOptions={ganaOptions}
                        lookupLoading={linkLookupLoading}
                      />
                    </Space>
                  ),
                },
                {
                  key: 'bartik',
                  label: 'Bartik',
                  children: (
                    <BartikCrudSection
                      ashtadhyayiSutraId={detail.id}
                      rows={detail.bartiks}
                      sutraPickerSeeds={linkedSutraPickerSeeds}
                      onChanged={reloadDetail}
                      onOpenLinkedSutra={(sutraId) => void openDetail(sutraId)}
                      barnaRows={barnaRows}
                      bhedRows={bhedRows}
                      pratyaharOptions={pratyaharOptions}
                      barnaSamuhaOptions={barnaSamuhaOptions}
                      ganaOptions={ganaOptions}
                      lookupLoading={linkLookupLoading}
                    />
                  ),
                },
                {
                  key: 'sutra-commentary-text',
                  label: 'Sutra commentary text',
                  children: (
                    <SutraCommentaryTextCrudSection
                      sutraId={detail.id}
                      rows={detail.sutraCommentaryTexts}
                      commentaryOptions={sutraCommentaryOptions}
                      languageOptions={languageOptions}
                      lookupLoading={linkLookupLoading}
                      onChanged={reloadDetail}
                    />
                  ),
                },
                {
                  key: 'an',
                  label: 'an',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {detail.an ? (
                        <Typography.Paragraph
                          type="secondary"
                          style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 12 }}
                        >
                          Raw <code>an</code>: {detail.an}
                        </Typography.Paragraph>
                      ) : null}
                      <AnubrittiCrudSection
                        ashtadhyayiSutraId={detail.id}
                        rows={detail.anubrittis}
                        sutraPickerSeeds={linkedSutraPickerSeeds}
                        padachhedPickerSeeds={anubrittiPadachhedPickerSeeds}
                        onChanged={reloadDetail}
                        onOpenLinkedSutra={(sutraId) => void openDetail(sutraId)}
                      />
                    </Space>
                  ),
                },
                {
                  key: 'ad',
                  label: 'ad',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {detail.ad ? (
                        <Typography.Paragraph
                          type="secondary"
                          style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 12 }}
                        >
                          Raw <code>ad</code>: {detail.ad}
                        </Typography.Paragraph>
                      ) : null}
                      <AdhikarCrudSection
                        ashtadhyayiSutraId={detail.id}
                        rows={detail.adhikars}
                        sutraPickerSeeds={linkedSutraPickerSeeds}
                        onChanged={reloadDetail}
                        onOpenLinkedSutra={(sutraId) => void openDetail(sutraId)}
                      />
                    </Space>
                  ),
                },
                {
                  key: 'meta',
                  label: 'Metadata',
                  children: (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      {(
                        [
                          ['skn', detail.skn],
                          ['lskn', detail.lskn],
                          ['mskn', detail.mskn],
                          ['sskn', detail.sskn],
                          ['plskn', detail.plskn],
                          ['lpn', detail.lpn],
                          ['skChapter', detail.skChapter],
                          ['lskChapter', detail.lskChapter],
                          ['rpn', detail.rpn],
                          ['psn', detail.psn],
                          ['psChapter', detail.psChapter],
                        ] as const
                      ).map(([k, v]) => (
                        <Typography.Paragraph key={k} style={{ marginBottom: 4 }}>
                          <Typography.Text strong>{k}:</Typography.Text> {v || '—'}
                        </Typography.Paragraph>
                      ))}
                    </Space>
                  ),
                },
              ]}
            />
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? `Edit sūtra #${editing.id}` : 'New sūtra'}
        open={editOpen}
        onCancel={closeEdit}
        onOk={() => void submitForm()}
        confirmLoading={saving}
        width={720}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          All fields are required for create; use empty strings where the source has &quot;&quot;. PATCH sends only changed
          fields.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" preserve={false} initialValues={emptyCreateAshtadhyayiBody()}>
          <SutraFormFields />
        </Form>
      </Modal>
    </Card>
  );
}
